import { useState, type FormEvent } from 'react';
import { useNavigate, Navigate } from '../lib/routing';
import { authClient } from '../lib/auth-client';
import { useAuth } from '../hooks/use-auth';
import { humanizeAuthError } from '../lib/humanize';
import { isStrongPassword, PASSWORD_MIN_LENGTH, PASSWORD_TOO_SHORT_MESSAGE } from '../lib/password';
import PasswordChecklist from '../components/password-checklist';
import { NaiaMark } from '../components/header/icons';
import { Alert } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Eye, EyeSlash } from '@phosphor-icons/react';

type TabMode = 'in' | 'up';

export default function SignIn() {
  const { session, loading } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState<TabMode>('in');
  const [forgotMode, setForgotMode] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);

  const isPreviewFakeSession = (session as { user?: { email?: string } } | null)?.user?.email?.endsWith('@preview.naia') ?? false;
  if (!loading && session && !isPreviewFakeSession) {
    return <Navigate to="/app" replace />;
  }

  const resetFeedback = () => { setError(null); setInfo(null); };

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    resetFeedback();

    if (forgotMode) {
      if (!email.trim()) { setError('Saisissez votre email pour recevoir le lien.'); return; }
      setBusy(true);
      try {
        const { error: authError } = await authClient.resetPasswordForEmail(email.trim(), {
          redirectTo: window.location.origin,
        });
        if (authError) { setError(humanizeAuthError(authError.message)); return; }
        setInfo("Si un compte existe pour cet email, un lien de réinitialisation vient d'être envoyé. Consultez votre boîte mail ✉️");
      } catch (e2) {
        setError(humanizeAuthError(e2 instanceof Error ? e2.message : String(e2)));
      } finally { setBusy(false); }
      return;
    }

    if (!email.trim() || !password) { setError('Renseignez votre email et votre mot de passe.'); return; }
    if (tab === 'up' && !isStrongPassword(password)) {
      setError(PASSWORD_TOO_SHORT_MESSAGE); return;
    }

    setBusy(true);
    try {
      const { error: authError } = tab === 'in'
        ? await authClient.signIn(email.trim(), password)
        : await authClient.signUp(email.trim(), password);
      if (authError) { setError(humanizeAuthError(authError.message)); return; }
      navigate('/app');
    } catch (e2) {
      setError(humanizeAuthError(e2 instanceof Error ? e2.message : String(e2)));
    } finally { setBusy(false); }
  };

  const ctaLabel = forgotMode ? 'Envoyer le lien' : tab === 'in' ? 'Me connecter' : 'Créer mon compte';

  return (
    <div className="min-h-dvh bg-background flex flex-col items-center justify-center px-4 py-10">
      <div className="w-full max-w-[420px]">
        <div className="flex flex-col items-center mb-8">
          <NaiaMark size={40} />
          <h1 className="mt-2 text-[17px] font-extrabold tracking-[0.06em] text-foreground">NAIA-EDU</h1>
          <span className="text-[13px] text-muted-foreground mt-0.5">Plateforme pédagogique intelligente</span>
        </div>

        <div className="bg-card border border-border rounded-card shadow-card p-6">
          {forgotMode ? (
            <>
              <h2 className="font-extrabold text-[16px] text-foreground mb-1">Mot de passe oublié</h2>
              <p className="text-[13px] text-muted-foreground mb-5">
                Saisissez votre email : nous vous envoyons un lien pour choisir un nouveau mot de passe.
              </p>

              <form onSubmit={submit} noValidate>
                <Field label="Email">
                  <Input type="email" autoComplete="email" value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="votre.email@example.fr" disabled={busy} />
                </Field>

                {info && <Alert className="mb-4">{info}</Alert>}
                {error && <Alert className="mb-4">{error}</Alert>}

                <Button type="submit" disabled={busy} className="w-full font-black">
                  {busy ? 'Envoi…' : ctaLabel}
                </Button>
                <button type="button"
                  onClick={() => { setForgotMode(false); resetFeedback(); }}
                  className="block w-full mt-2 py-3 bg-transparent border-none text-primary font-bold text-[13px] underline cursor-pointer font-[inherit] text-center">
                  ← Retour à la connexion
                </button>
              </form>
            </>
          ) : (
            <Tabs value={tab} onValueChange={(v) => { setTab(v as TabMode); resetFeedback(); }}>
              <TabsList className="w-full mb-5">
                <TabsTrigger value="in" className="flex-1">Connexion</TabsTrigger>
                <TabsTrigger value="up" className="flex-1">Inscription</TabsTrigger>
              </TabsList>

              {(['in', 'up'] as TabMode[]).map((t) => (
                <TabsContent key={t} value={t}>
                  <form onSubmit={submit} noValidate>
                    <Field label="Email">
                      <Input type="email" autoComplete="email" value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="votre.email@example.fr" disabled={busy} />
                    </Field>

                    <Field label="Mot de passe">
                      <div className="relative">
                        <Input
                          type={showPw ? 'text' : 'password'}
                          autoComplete={t === 'in' ? 'current-password' : 'new-password'}
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          placeholder={t === 'up' ? `${PASSWORD_MIN_LENGTH} caractères minimum` : 'Votre mot de passe'}
                          disabled={busy}
                          className="pr-11"
                        />
                        <button type="button"
                          onClick={() => setShowPw((v) => !v)}
                          aria-label={showPw ? 'Masquer le mot de passe' : 'Afficher le mot de passe'}
                          className="absolute right-0 top-0 w-10 h-full grid place-items-center bg-transparent border-none cursor-pointer text-muted-foreground hover:text-foreground transition-colors">
                          {showPw ? <EyeSlash size={17} /> : <Eye size={17} />}
                        </button>
                      </div>
                      {t === 'up' && password.length > 0 && <PasswordChecklist password={password} />}
                    </Field>

                    {t === 'in' && (
                      <button type="button"
                        onClick={() => { setForgotMode(true); resetFeedback(); }}
                        className="bg-transparent border-none min-h-11 text-primary font-bold text-[13px] underline cursor-pointer font-[inherit] -mt-1 mb-1 block">
                        Mot de passe oublié ?
                      </button>
                    )}

                    {info && <Alert className="mb-4">{info}</Alert>}
                    {error && <Alert className="mb-4">{error}</Alert>}

                    <Button type="submit" disabled={busy} className="w-full font-black text-[16px] mt-1">
                      {busy ? 'Patience…' : ctaLabel}
                    </Button>
                  </form>
                </TabsContent>
              ))}
            </Tabs>
          )}
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="mb-4">
      <label className="block mb-1.5 font-bold text-foreground text-[13px]">{label}</label>
      {children}
    </div>
  );
}
